'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';

export default function AuthLayout() {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');

  return (
    <div className="min-h-screen flex">
      {/* Left Panel — Brand */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 gradient-primary flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full border border-white/10 -translate-y-1/3 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full border border-white/10 translate-y-1/3 -translate-x-1/3" />

        <div className="relative z-10">
          <Link href="/" className="flex items-center gap-3">
            <AppLogo size={44} />
            <div>
              <div className="text-white font-bold text-xl leading-none">Maritzburg</div>
              <div className="text-accent text-sm font-medium tracking-widest uppercase">Golf Club</div>
            </div>
          </Link>
        </div>

        <div className="relative z-10 flex-1 flex flex-col justify-center py-12">
          <div className="inline-flex items-center gap-2 bg-accent/20 border border-accent/30 rounded-full px-3 py-1.5 mb-6 w-fit">
            <Icon name="StarIcon" size={14} variant="solid" className="text-accent" />
            <span className="text-accent text-xs font-semibold tracking-wide uppercase">Visitor Portal</span>
          </div>
          <h1 className="text-4xl xl:text-5xl font-extrabold text-white leading-tight mb-4">
            Your Game,<br />
            <span className="text-accent">Your Club.</span>
          </h1>
          <p className="text-white/70 text-base leading-relaxed mb-8 max-w-sm">
            Create your visitor profile to book tee times, track your rounds, earn loyalty rewards, and access exclusive promotions at Maritzburg Golf Club.
          </p>

          <div className="space-y-4">
            {[
              { icon: 'CalendarDaysIcon', title: 'Instant Online Booking', sub: 'Book tee times 24 months in advance' },
              { icon: 'TrophyIcon', title: 'Loyalty Rewards', sub: 'Earn points toward complimentary rounds' },
              { icon: 'ChartBarIcon', title: 'Round Tracking', sub: 'Monitor your rounds and handicap progress' },
              { icon: 'BellIcon', title: 'Event Notifications', sub: 'First to know about competitions and specials' },
            ].map((item) => (
              <div key={`benefit-${item.title}`} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                  <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={18} variant="outline" className="text-accent" />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">{item.title}</div>
                  <div className="text-white/50 text-xs">{item.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 bg-white/10 border border-white/20 rounded-xl p-4">
            <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center shrink-0 text-primary font-bold text-sm">TN</div>
            <div className="flex-1">
              <div className="text-white text-sm font-semibold">Thabo Nkosi</div>
              <div className="text-white/60 text-xs">22 rounds played · Loyalty member</div>
            </div>
            <div className="text-right">
              <div className="text-accent font-bold text-sm font-tabular">220 pts</div>
              <div className="text-white/50 text-xs">Gold tier</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex flex-col bg-background overflow-y-auto">
        {/* Mobile Header */}
        <div className="lg:hidden gradient-primary p-5 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2.5">
            <AppLogo size={32} />
            <div>
              <div className="text-white font-bold text-sm leading-none">Maritzburg</div>
              <div className="text-accent text-xs font-medium tracking-widest uppercase">Golf Club</div>
            </div>
          </Link>
          <Link href="/online-booking" className="text-white/70 text-xs font-medium hover:text-white transition-colors">
            Book a Round
          </Link>
        </div>

        <div className="flex-1 flex flex-col justify-center px-5 py-8 sm:px-8 lg:px-12 xl:px-16 max-w-xl w-full mx-auto lg:mx-0 lg:max-w-none">
          {/* Tab Switcher */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-1">
              {activeTab === 'login' ? 'Welcome Back' : 'Create Your Profile'}
            </h2>
            <p className="text-muted-foreground text-sm">
              {activeTab === 'login' ?'Sign in to access your bookings, loyalty points, and profile.' :'Join Maritzburg Golf Club as a registered visitor.'}
            </p>

            <div className="flex mt-5 bg-muted rounded-xl p-1 w-fit">
              {(['login', 'register'] as const).map((tab) => (
                <button
                  key={`tab-${tab}`}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    activeTab === tab
                      ? 'bg-card text-foreground shadow-card'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab === 'login' ? 'Sign In' : 'Register'}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'login' ? (
            <LoginForm onSwitchToRegister={() => setActiveTab('register')} />
          ) : (
            <RegisterForm onSwitchToLogin={() => setActiveTab('login')} />
          )}
        </div>
      </div>
    </div>
  );
}